import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { API_ENDPOINTS } from "../../config";
import { useSearchParams } from "react-router-dom";
import { timezoneMap } from "../../utils/timezoneMap";

// Add reference to auth status change event
const AUTH_STATUS_CHANGED = "auth_status_changed";

const UserConfigModal = ({ userId, onConfigSaved }) => {
  const [formData, setFormData] = useState({
    full_name: localStorage.getItem("user_name") || "",
    background: "",
    email_fetch_days: "1",
    meeting_start_time: "09:00",
    meeting_end_time: "17:00",
    max_threshold: "",
    meeting_duration: "30",
    email_preferences: "",
    phone_number: "",
    timezone: "",
    messaging_option: "sms",
    vip_list: [""], // Initialize with one empty field
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [searchParams] = useSearchParams();

  // Check if this is a new user from OAuth
  useEffect(() => {
    // Look for parameters that indicate a recent OAuth signup
    const provider = searchParams.get("provider");
    const accessToken = searchParams.get("access_token");
    const isNewSignup = sessionStorage.getItem("new_user_signup") === "true";

    if (
      isNewSignup ||
      (provider &&
        accessToken &&
        sessionStorage.getItem("oauth_signup") === "true")
    ) {
      setIsNewUser(true);
      // Clear the signup flags once we've detected them
      sessionStorage.removeItem("new_user_signup");
      sessionStorage.removeItem("oauth_signup");
      console.log("Detected new user signup");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const token = Cookies.get("access_token");
      if (!token) {
        console.error("No access token found");
        return;
      }

      // Filter out empty email addresses from vip_list
      const filteredVipList = formData.vip_list.filter(
        (email) => email.trim() !== ""
      );

      const response = await axios.post(
        API_ENDPOINTS.USER_CONFIG,
        {
          full_name: formData.full_name,
          background: formData.background,
          meeting_preferences: {
            start: parseInt(formData.meeting_start_time.split(":")[0]),
            end: parseInt(formData.meeting_end_time.split(":")[0]),
            duration: parseInt(formData.meeting_duration || "30"),
          },
          max_threshold_meet: formData.max_threshold,
          email_preferences: formData.email_preferences,
          email_fetch_days: parseInt(formData.email_fetch_days),
          phone_number: formData.phone_number,
          timezone: formData.timezone,
          vip_list: filteredVipList,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data) {
        console.log("Configuration saved successfully:", response.data);

        // Update cached user data with the new name
        const cachedData = localStorage.getItem("cached_user_data");
        if (cachedData) {
          const userData = JSON.parse(cachedData);
          userData.name = formData.full_name;
          localStorage.setItem("cached_user_data", JSON.stringify(userData));
          localStorage.setItem("user_name", formData.full_name);
        }

        // Dispatch auth status changed event to refresh UI components
        window.dispatchEvent(new CustomEvent(AUTH_STATUS_CHANGED));

        // Call onConfigSaved to close the modal
        if (onConfigSaved) {
          onConfigSaved();
        }
      }
    } catch (error) {
      console.error("Error details:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error(
        "Error saving config:",
        error.response?.data || error.message
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle changes to VIP list emails
  const handleVipEmailChange = (index, value) => {
    const updatedVipList = [...formData.vip_list];
    updatedVipList[index] = value;
    setFormData((prev) => ({
      ...prev,
      vip_list: updatedVipList,
    }));
  };

  // Add a new empty field to the VIP list
  const addVipEmailField = () => {
    setFormData((prev) => ({
      ...prev,
      vip_list: [...prev.vip_list, ""],
    }));
  };

  // Remove a specific field from the VIP list
  const removeVipEmailField = (index) => {
    const updatedVipList = [...formData.vip_list];
    updatedVipList.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      vip_list: updatedVipList,
    }));
  };

  const detectUserTimezone = () => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log("The timezone is", timezone);

      if (timezone) {
        // Check if we have a direct mapping for this timezone
        if (timezoneMap[timezone]) {
          setFormData((prev) => ({
            ...prev,
            timezone: timezoneMap[timezone],
          }));
          return;
        }

        // If no direct mapping, get the GMT offset
        const now = new Date();
        const offset = -now.getTimezoneOffset() / 60;

        // Format the offset as GMT+X or GMT-X
        const sign = offset >= 0 ? "+" : "-";
        const absOffset = Math.abs(offset);
        const hours = Math.floor(absOffset);
        const minutes = (absOffset - hours) * 60;

        let offsetString = `GMT${sign}${hours}`;
        if (minutes > 0) {
          offsetString += `:${minutes}`;
        }

        console.log("Calculated timezone offset:", offsetString);

        // Check if this offset is in our options
        const offsetInOptions = document.querySelector(
          `option[value="${offsetString}"]`
        );
        if (offsetInOptions) {
          setFormData((prev) => ({
            ...prev,
            timezone: offsetString,
          }));
        } else {
          // If we can't find the exact offset, round to the nearest hour
          const roundedOffset = `GMT${sign}${hours}`;
          console.log("Rounded timezone offset:", roundedOffset);

          setFormData((prev) => ({
            ...prev,
            timezone: roundedOffset,
          }));
        }
      }
    } catch (error) {
      console.error("Error detecting timezone:", error);
      alert(
        "Could not automatically detect your timezone. Please select it manually."
      );
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[1000] p-4 sm:p-0">
      <div className="absolute inset-0 bg-black/30" />{" "}
      {/* Semi-transparent overlay */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-[95%] sm:max-w-md relative z-10 pointer-events-auto overflow-y-auto max-h-[90vh]">
        <h2 className="text-lg font-bold mb-2">Email Configuration</h2>
        <p className="text-sm text-gray-600 mb-4">
          {isNewUser
            ? "Welcome! Please complete this required configuration before continuing."
            : "Configure AI email assist to better sort and manage your emails"}
        </p>
        <form onSubmit={handleSubmit} className="space-y-2">
          <div>
            <label className="block text-xs font-medium mb-0.5 p-1">
              Full Name
            </label>
            <input
              key="full-name-input"
              type="text"
              placeholder="Enter your full name"
              value={formData.full_name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, full_name: e.target.value }))
              }
              className="w-full p-1 rounded text-sm bg-gray-100"
              required
            />
          </div>
          <div>
            <label
              htmlFor="background-input"
              className="block text-xs font-medium mb-0.5 p-1"
            >
              Professional Background
            </label>
            <textarea
              key="background-textarea"
              value={formData.background}
              placeholder="Enter your professional background including Title and 0rganization"
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, background: e.target.value }))
              }
              className="w-full p-1 rounded text-sm bg-gray-100"
              required
              rows={2}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-0.5 p-1">
              Meeting Preferences
            </label>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <input
                  key="meeting-start-time"
                  type="time"
                  value={formData.meeting_start_time}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      meeting_start_time: e.target.value,
                    }))
                  }
                  className="p-1 border rounded text-sm"
                />
                <span className="px-1">to</span>
                <input
                  key="meeting-end-time"
                  type="time"
                  value={formData.meeting_end_time}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      meeting_end_time: e.target.value,
                    }))
                  }
                  className="p-1 border rounded text-sm"
                />
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="max-threshold-input"
              className="block text-xs font-medium mb-0.5 p-1"
            >
              Max threshold of meetings
            </label>
            <input
              key="max-threshold-input"
              type="text"
              value={formData.max_threshold}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  max_threshold: e.target.value,
                }))
              }
              className="w-full p-1 rounded text-sm bg-gray-100"
              placeholder="max threshold of meetings (e.g 4 hours)"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-0.5 p-1">
              Email Preferences
            </label>
            <input
              key="email-preferences-input"
              type="text"
              value={formData.email_preferences}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  email_preferences: e.target.value,
                }))
              }
              className="w-full p-1 rounded text-sm bg-gray-100"
              placeholder="e.g., Prefer concise emails, formal tone"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-0.5 p-1">
              Email Fetch Days (1–7)
            </label>
            <input
              key="email-fetch-days"
              type="number"
              name="email_fetch_days"
              min="1"
              max="7"
              value={formData.email_fetch_days}
              onChange={handleChange}
              className="w-full p-1 rounded text-sm bg-gray-100"
              required
            />
            <p className="text-xs text-gray-500 mt-0.5">
              How many days of email to fetch (max 7)
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium mb-0.5 p-1">
              Phone Number
            </label>
            <input
              key="phone-number-input"
              type="tel"
              value={formData.phone_number}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  phone_number: e.target.value,
                }))
              }
              className="w-full p-1 rounded text-sm bg-gray-100"
              placeholder="+1 (123) 456-7890"
            />
            <p className="text-xs text-gray-500 mt-0.5">
              Include country code (e.g., +1 for US)
            </p>
          </div>
          <div className="mb-4">
            <label
              htmlFor="messaging_option"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Messaging Option
            </label>
            <select
              id="messaging_option"
              name="messaging_option"
              value={formData.messaging_option}
              onChange={handleChange}
              className="w-full p-1 rounded text-sm bg-gray-100"
            >
              <option value="sms">SMS Notifications</option>
              <option value="slack" disabled>
                Slack Notifications
              </option>
              <option value="teams" disabled>
                Microsoft Teams Notifications
              </option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Select your preferred platform for receiving notifications
            </p>
          </div>

          {/* VIP List section */}
          <div>
            <label className="block text-xs font-medium mb-0.5 p-1">
              VIP List
            </label>
            <p className="text-xs text-gray-500 mb-1">
              Add email addresses that should receive priority treatment
            </p>

            {formData.vip_list.map((email, index) => (
              <div
                key={`vip-email-${index}`}
                className="flex items-center mb-1"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleVipEmailChange(index, e.target.value)}
                  placeholder="Enter email address"
                  className="flex-grow p-1 rounded text-sm bg-gray-100 mr-1"
                />
                <button
                  type="button"
                  onClick={() => removeVipEmailField(index)}
                  className="p-1 text-red-500 hover:text-red-700"
                  title="Remove"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addVipEmailField}
              className="text-xs text-blue-500 hover:text-blue-700 mt-1 flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add another email
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium mb-0.5 p-1">
              Timezone
            </label>
            <select
              key="timezone-select"
              value={formData.timezone}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, timezone: e.target.value }))
              }
              className="w-full p-1 rounded text-sm bg-gray-100"
              required
            >
              <option value="">Select timezone</option>
              <option value="Asia/Karachi">Asia/Karachi (GMT+5)</option>
              <option value="Asia/Kolkata">Asia/Kolkata (GMT+5:30)</option>
              <option value="Asia/Dubai">Asia/Dubai (GMT+4)</option>
              <option value="Asia/Riyadh">Asia/Riyadh (GMT+3)</option>
              <option value="Asia/Baghdad">Asia/Baghdad (GMT+3)</option>
              <option value="Asia/Tehran">Asia/Tehran (GMT+3:30)</option>
              <option value="Asia/Kabul">Asia/Kabul (GMT+4:30)</option>
              <option value="Asia/Tashkent">Asia/Tashkent (GMT+5)</option>
              <option value="Asia/Yekaterinburg">
                Asia/Yekaterinburg (GMT+5)
              </option>
              <option value="Asia/Dhaka">Asia/Dhaka (GMT+6)</option>
              <option value="Asia/Yangon">Asia/Yangon (GMT+6:30)</option>
              <option value="Asia/Bangkok">Asia/Bangkok (GMT+7)</option>
              <option value="Asia/Jakarta">Asia/Jakarta (GMT+7)</option>
              <option value="Asia/Singapore">Asia/Singapore (GMT+8)</option>
              <option value="Asia/Kuala_Lumpur">
                Asia/Kuala_Lumpur (GMT+8)
              </option>
              <option value="Asia/Shanghai">Asia/Shanghai (GMT+8)</option>
              <option value="Asia/Hong_Kong">Asia/Hong_Kong (GMT+8)</option>
              <option value="Asia/Taipei">Asia/Taipei (GMT+8)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (GMT+9)</option>
              <option value="Asia/Seoul">Asia/Seoul (GMT+9)</option>
              <option value="Asia/Pyongyang">Asia/Pyongyang (GMT+9)</option>

              <option value="Australia/Perth">Australia/Perth (GMT+8)</option>
              <option value="Australia/Darwin">
                Australia/Darwin (GMT+9:30)
              </option>
              <option value="Australia/Adelaide">
                Australia/Adelaide (GMT+9:30)
              </option>
              <option value="Australia/Brisbane">
                Australia/Brisbane (GMT+10)
              </option>
              <option value="Australia/Sydney">
                Australia/Sydney (GMT+10)
              </option>
              <option value="Australia/Melbourne">
                Australia/Melbourne (GMT+10)
              </option>
              <option value="Australia/Hobart">
                Australia/Hobart (GMT+10)
              </option>
              <option value="Pacific/Auckland">
                Pacific/Auckland (GMT+12)
              </option>
              <option value="Pacific/Fiji">Pacific/Fiji (GMT+12)</option>
              <option value="Pacific/Honolulu">
                Pacific/Honolulu (GMT-10)
              </option>

              <option value="America/New_York">America/New_York (EST)</option>
              <option value="America/Chicago">America/Chicago (CST)</option>
              <option value="America/Denver">America/Denver (MST)</option>
              <option value="America/Los_Angeles">
                America/Los_Angeles (PST)
              </option>
              <option value="America/Anchorage">
                America/Anchorage (GMT-9)
              </option>
              <option value="America/Halifax">America/Halifax (GMT-4)</option>
              <option value="America/St_Johns">
                America/St_Johns (GMT-3:30)
              </option>
              <option value="America/Toronto">America/Toronto (GMT-5)</option>
              <option value="America/Winnipeg">America/Winnipeg (GMT-6)</option>
              <option value="America/Edmonton">America/Edmonton (GMT-7)</option>
              <option value="America/Vancouver">
                America/Vancouver (GMT-8)
              </option>
              <option value="America/Mexico_City">
                America/Mexico_City (GMT-6)
              </option>
              <option value="America/Bogota">America/Bogota (GMT-5)</option>
              <option value="America/Lima">America/Lima (GMT-5)</option>
              <option value="America/Caracas">America/Caracas (GMT-4)</option>
              <option value="America/Santiago">America/Santiago (GMT-4)</option>
              <option value="America/Buenos_Aires">
                America/Buenos_Aires (GMT-3)
              </option>
              <option value="America/Sao_Paulo">
                America/Sao_Paulo (GMT-3)
              </option>

              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="Europe/Dublin">Europe/Dublin (GMT)</option>
              <option value="Europe/Lisbon">Europe/Lisbon (GMT)</option>
              <option value="Europe/Madrid">Europe/Madrid (GMT+1)</option>
              <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
              <option value="Europe/Brussels">Europe/Brussels (GMT+1)</option>
              <option value="Europe/Amsterdam">Europe/Amsterdam (GMT+1)</option>
              <option value="Europe/Berlin">Europe/Berlin (GMT+1)</option>
              <option value="Europe/Rome">Europe/Rome (GMT+1)</option>
              <option value="Europe/Zurich">Europe/Zurich (GMT+1)</option>
              <option value="Europe/Vienna">Europe/Vienna (GMT+1)</option>
              <option value="Europe/Prague">Europe/Prague (GMT+1)</option>
              <option value="Europe/Warsaw">Europe/Warsaw (GMT+1)</option>
              <option value="Europe/Budapest">Europe/Budapest (GMT+1)</option>
              <option value="Europe/Stockholm">Europe/Stockholm (GMT+1)</option>
              <option value="Europe/Copenhagen">
                Europe/Copenhagen (GMT+1)
              </option>
              <option value="Europe/Oslo">Europe/Oslo (GMT+1)</option>
              <option value="Europe/Helsinki">Europe/Helsinki (GMT+2)</option>
              <option value="Europe/Athens">Europe/Athens (GMT+2)</option>
              <option value="Europe/Bucharest">Europe/Bucharest (GMT+2)</option>
              <option value="Europe/Kiev">Europe/Kiev (GMT+2)</option>
              <option value="Europe/Moscow">Europe/Moscow (GMT+3)</option>
              <option value="Europe/Istanbul">Europe/Istanbul (GMT+3)</option>

              <option value="Africa/Cairo">Africa/Cairo (GMT+2)</option>
              <option value="Africa/Johannesburg">
                Africa/Johannesburg (GMT+2)
              </option>
              <option value="Africa/Nairobi">Africa/Nairobi (GMT+3)</option>
              <option value="Africa/Lagos">Africa/Lagos (GMT+1)</option>
              <option value="Africa/Casablanca">Africa/Casablanca (GMT)</option>
              <option value="Africa/Tunis">Africa/Tunis (GMT+1)</option>
              <option value="Africa/Algiers">Africa/Algiers (GMT+1)</option>
              <option value="Africa/Accra">Africa/Accra (GMT)</option>
              <option value="Africa/Dakar">Africa/Dakar (GMT)</option>

              <option value="Asia/Jerusalem">Asia/Jerusalem (GMT+2)</option>
              <option value="Asia/Beirut">Asia/Beirut (GMT+2)</option>
              <option value="Asia/Damascus">Asia/Damascus (GMT+2)</option>
              <option value="Asia/Amman">Asia/Amman (GMT+2)</option>
              <option value="Asia/Qatar">Asia/Qatar (GMT+3)</option>
            </select>
            <button
              type="button"
              onClick={detectUserTimezone}
              className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white mt-2 text-xs rounded transition-colors whitespace-nowrap"
            >
              Get my timezone
            </button>
          </div>
          <div className="flex flex-wrap justify-start gap-2 mt-4">
            {!isNewUser && (
              <button
                type="button"
                onClick={onConfigSaved}
                className="px-4 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors text-sm"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm flex items-center justify-center min-w-[100px]"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : isNewUser ? (
                "Start Using Efficia"
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserConfigModal;
